import { useMemo, type ReactNode } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';

// Analytics a wholesale brand actually reads: what sells, who buys, what's owed,
// and where orders sit. All derived from the orders + invoices already loaded —
// no extra calls. Each panel hides itself when it has nothing to show.

const money = (c: number) => '$' + Math.round((c || 0) / 1000) + 'k';
const moneyFull = (c: number) => '$' + Math.round((c || 0)).toLocaleString('en-US');
const PALETTE = ['#6366f1', '#14b8a6', '#f59e0b', '#8b5cf6', '#0ea5e9', '#ec4899'];

function Panel({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="card p-4">
      <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      {sub && <p className="text-xs text-slate-400 mb-2">{sub}</p>}
      <div className="mt-2" style={{ height: 196 }}>{children}</div>
    </div>
  );
}

export default function MakerAnalytics({ orders = [], invoices = [] }: { orders?: any[]; invoices?: any[] }) {
  const live = useMemo(() => orders.filter((o) => o.status !== 'cancelled' && !o.onConsignment), [orders]);
  const recognized = (o: any) => o.freightDelivered || o.invoiceSent || o.paymentReceived;

  const skuSales = useMemo(() => {
    const m: Record<string, number> = {};
    for (const o of live) for (const it of o.items || []) {
      const n = String(it.name || '—').replace(/ — case of.*/i, '').replace(/ granola/i, '');
      m[n] = (m[n] || 0) + (it.totalPriceCents || 0);
    }
    return Object.entries(m).map(([name, v]) => ({ name, value: Math.round(v / 100) })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [live]);

  const custRev = useMemo(() => {
    const m: Record<string, number> = {};
    for (const o of live) if (recognized(o)) {
      const n = String(o.customer?.name || o.customerName || '—').replace(/,.*$/, '');
      m[n] = (m[n] || 0) + (o.totalCents || 0);
    }
    return Object.entries(m).map(([name, v]) => ({ name, value: Math.round(v / 100) })).sort((a, b) => b.value - a.value).slice(0, 5);
  }, [live]);

  const aging = useMemo(() => {
    const b = [{ name: 'Current', value: 0 }, { name: '1–30', value: 0 }, { name: '31–60', value: 0 }, { name: '60+', value: 0 }];
    for (const i of invoices) {
      if (i.status === 'paid' || i.status === 'void') continue;
      const out = (i.totalCents || 0) - (i.amountPaidCents || 0);
      if (out <= 0) continue;
      const t = new Date(i.sentAt || i.issuedAt || i.createdAt).getTime();
      const days = Number.isFinite(t) ? Math.floor((Date.now() - t) / 86400000) : 0;
      const idx = days <= 30 ? 0 : days <= 60 ? 1 : days <= 90 ? 2 : 3;
      b[idx].value += Math.round(out / 100);
    }
    return b.some((x) => x.value > 0) ? b : [];
  }, [invoices]);

  const pipeline = useMemo(() => {
    const order = ['draft', 'confirmed', 'fulfilled', 'partially_fulfilled', 'invoiced'];
    const label: Record<string, string> = { draft: 'Draft', confirmed: 'Confirmed', fulfilled: 'Fulfilled', partially_fulfilled: 'Partial', invoiced: 'Invoiced' };
    const m: Record<string, number> = {};
    for (const o of orders) { if (o.status === 'cancelled') continue; m[o.status] = (m[o.status] || 0) + 1; }
    return order.filter((s) => m[s]).map((s) => ({ name: label[s] || s, value: m[s] }));
  }, [orders]);

  const revByMonth = useMemo(() => {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const done = live.filter(recognized);
    const years = done.map((o) => new Date(o.fulfillmentDate || o.createdAt).getFullYear()).filter((y) => Number.isFinite(y));
    const yr = years.length ? Math.max(...years) : new Date().getFullYear();
    const m = Array(12).fill(0);
    for (const o of done) {
      const t = new Date(o.fulfillmentDate || o.createdAt);
      if (!Number.isFinite(t.getTime()) || t.getFullYear() !== yr) continue;
      m[t.getMonth()] += o.totalCents || 0;
    }
    const idxs = m.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
    if (!idxs.length) return [];
    const lo = Math.min(...idxs), hi = Math.max(...idxs);
    return m.slice(lo, hi + 1).map((v, i) => ({ name: MONTHS[lo + i], value: Math.round(v / 100) }));
  }, [live]);

  if (!skuSales.length && !custRev.length && !aging.length && !pipeline.length && !revByMonth.length) return null;

  const axis = { fontSize: 11, fill: '#94a3b8' } as const;
  const dollarTip = (v: any) => [moneyFull(Number(v)), 'Revenue'];

  return (
    <div className="space-y-3">
      <h2 className="font-semibold text-slate-900">Analytics</h2>
      {revByMonth.length > 0 && (
        <Panel title="Revenue by month" sub="Recognized wholesale revenue">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revByMonth} margin={{ left: 4, right: 8, top: 8, bottom: 4 }}>
              <XAxis dataKey="name" tick={axis} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => money(Number(v))} />
              <Tooltip formatter={(v: any) => [moneyFull(Number(v)), 'Revenue']} cursor={{ fill: '#f1f5f9' }} />
              <Bar dataKey="value" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {skuSales.length > 0 && (
          <Panel title="Sales by product" sub="Revenue per SKU, all orders">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={skuSales} layout="vertical" margin={{ left: 8, right: 36, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={118} tick={axis} tickLine={false} axisLine={false} />
                <Tooltip formatter={dollarTip} cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={18}>
                  {skuSales.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  <LabelList dataKey="value" position="right" formatter={(v: any) => money(Number(v))} style={{ fontSize: 10, fill: '#64748b' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}

        {custRev.length > 0 && (
          <Panel title="Top customers" sub="Recognized revenue by account">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={custRev} layout="vertical" margin={{ left: 8, right: 36, top: 4, bottom: 4 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={118} tick={axis} tickLine={false} axisLine={false} />
                <Tooltip formatter={dollarTip} cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="value" radius={[0, 5, 5, 0]} barSize={18} fill="#14b8a6">
                  <LabelList dataKey="value" position="right" formatter={(v: any) => money(Number(v))} style={{ fontSize: 10, fill: '#64748b' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}

        {aging.length > 0 && (
          <Panel title="Accounts receivable" sub="Outstanding by age (days since invoiced)">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aging} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
                <XAxis dataKey="name" tick={axis} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis tick={axis} tickLine={false} axisLine={false} width={44} tickFormatter={(v) => money(Number(v))} />
                <Tooltip formatter={(v: any) => [moneyFull(Number(v)), 'Outstanding']} cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="value" radius={[5, 5, 0, 0]} barSize={46}>
                  {aging.map((_, i) => <Cell key={i} fill={['#14b8a6', '#f59e0b', '#f97316', '#ef4444'][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}

        {pipeline.length > 0 && (
          <Panel title="Order pipeline" sub="Orders by stage">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pipeline} margin={{ left: 0, right: 8, top: 8, bottom: 4 }}>
                <XAxis dataKey="name" tick={axis} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis tick={axis} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                <Tooltip formatter={(v: any) => [v, 'Orders']} cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="value" radius={[5, 5, 0, 0]} barSize={40}>
                  {pipeline.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  <LabelList dataKey="value" position="top" style={{ fontSize: 11, fill: '#64748b' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        )}
      </div>
    </div>
  );
}
